import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './HomeEquipe.module.css';
import { PedidosContext } from "../../../shared/contexts/PedidosContext";
import CardUltimosPedidos from "../../../shared/components/CardUltimosPedidos";
import FooterOpcoes from '../../cliente/components/FooterOpcoes';
import HeaderPageEquipe from '../components/HeaderPageEquipe'
import { sseService } from '../../../shared/services/sse';
import { Receipt, ShoppingBag, LayoutGrid, Package } from 'lucide-react';
import { numeroExibicaoPedido } from '../../../shared/utils/pedido';

const ATALHOS = [
  { to: '/equipe/novo-pedido', label: 'Novo pedido', icon: ShoppingBag },
  { to: '/equipe/mapa-mesas', label: 'Mapa de mesas', icon: LayoutGrid },
  // "Chamar cozinha" desativado por enquanto — ver App.js
  { to: '/equipe/estoque', label: 'Estoque', icon: Package },
];


const HomeEquipe = () => {
  const nomeEstabelecimento = localStorage.getItem("nomeEstabelecimento");
  const idEstabelecimento = localStorage.getItem("estabelecimento_id");
  const { pedidos, setPedidos, loading, atualizarPedidos } = useContext(PedidosContext);

  const [notificacao, setNotificacao] = useState(null);

  // Solicitar permissão para notificações
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('🔔 Permissão de notificação:', permission);
      });
    }
  }, []);

  // Escutar novos pedidos via SSE
  useEffect(() => {
    // Conectar ao SSE do estabelecimento
    if (idEstabelecimento) {
      sseService.connect(idEstabelecimento);
      console.log(`🔌 Conectado ao SSE: estabelecimento_${idEstabelecimento}`);
    }

    // Handler para novo pedido
    const handleNovoPedido = (data) => {
      console.log('🔔 Novo pedido recebido:', data.pedido);

      // Mostrar notificação visual no app
      setNotificacao({
        mensagem: `Novo pedido #${numeroExibicaoPedido(data.pedido)}`,
        codigo: data.pedido.codigo_entrega
      });

      // Atualizar lista de pedidos
      if (atualizarPedidos) {
        atualizarPedidos();
      }

      // Mostrar notificação do navegador
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Novo Pedido!', {
          body: `Pedido #${numeroExibicaoPedido(data.pedido)} - Código: ${data.pedido.codigo_entrega}`,
          icon: '/logo192.png',
          tag: `pedido-${data.pedido.id_pedido}`,
          requireInteraction: true
        });
      }

      // Esconder notificação após 5 segundos
      setTimeout(() => {
        setNotificacao(null);
      }, 5000);
    };

    sseService.on('novo_pedido', handleNovoPedido);

    // Remover listener ao desmontar (não desconectar - SSE é compartilhado)
    return () => {
      sseService.off('novo_pedido', handleNovoPedido);
    };
  }, [idEstabelecimento, atualizarPedidos]);

  const hoje = new Date().toDateString();
  const pedidosDeHoje = pedidos.filter(p => p.data_pedido && new Date(p.data_pedido).toDateString() === hoje);

  const pedidosPendentes = pedidosDeHoje.filter(p => p.status?.toLowerCase() === 'pendente');
  const pedidosEmAndamento = pedidosDeHoje.filter(p => p.status?.toLowerCase() === 'em andamento').length;
  const pedidosProntos = pedidosDeHoje.filter(p => p.status?.toLowerCase() === 'pronto').length;
  const pedidosHoje = pedidosDeHoje.length;

  const pedidosPendentesOrdenados = [...pedidosPendentes]
    .sort((a, b) => new Date(b.data_pedido) - new Date(a.data_pedido));

  const mudarStatusPedido = async (id, novoStatus) => {
    const novosPedidos = pedidos.map(p =>
      p.id_pedido === id ? { ...p, status: novoStatus } : p
    );
    setPedidos(novosPedidos);
  };

  const aceitarPedido = async (pedido) => {
    const id_pedido = pedido.id_pedido;
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_URL}/atualizar-item`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ numPedido: id_pedido, novoStatus: "em andamento" }),
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.message || "Erro ao aceitar pedido.");
        return;
      }

      if (data.success) {
        setNotificacao({
          mensagem: `Pedido #${numeroExibicaoPedido(pedido)} aceito!`,
          codigo: 'Em andamento',
          tipo: 'sucesso'
        });
        setTimeout(() => setNotificacao(null), 3000);

        const novosPedidos = pedidos.map((pedido) =>
          pedido.id_pedido === id_pedido ? { ...pedido, status: "em andamento" } : pedido
        );
        setPedidos(novosPedidos);
      } else {
        alert(data.message || "Erro ao aceitar pedido.");
        // Sincroniza o estado com o banco para corrigir status desatualizado
        if (atualizarPedidos) atualizarPedidos();
      }
    } catch (error) {
      console.error("Erro ao aceitar pedido:", error);
      alert("Erro de comunicação com o servidor. Tente novamente.");
      if (atualizarPedidos) atualizarPedidos();
    }
  };

  return (
    <div className={styles.container}>
      {/* Notificação */}
      {notificacao && (
        <div className={styles.notificacao}>
          <div className={`${styles.notificacaoContent} ${notificacao.tipo === 'sucesso' ? styles.notificacaoSucesso : ''}`}>
            <span className={styles.notificacaoIcon}>{notificacao.tipo === 'sucesso' ? '✅' : '🔔'}</span>
            <div className={styles.notificacaoTexto}>
              <p className={styles.notificacaoTitulo}>{notificacao.mensagem}</p>
              <p className={styles.notificacaoCodigo}>{notificacao.codigo}</p>
            </div>
            <button
              className={styles.notificacaoFechar}
              onClick={() => setNotificacao(null)}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <HeaderPageEquipe
        nomeEstabelecimento={nomeEstabelecimento}
        nomePage="Visão Geral"
      />

      {/* Total de Hoje — destaque dentro do header vermelho */}
      <section className={styles.totalHojeSection}>
        <p className={styles.totalHojeLabel}>Total de Hoje</p>
        <div className={styles.totalHojeRow}>
          <h1 className={styles.totalHojeValue}>{pedidosHoje}</h1>
          <span className={styles.totalHojeSublabel}>pedidos</span>
        </div>
      </section>

      {/* Cards de Status */}
      <section className={styles.metricsSection}>
        <div className={`${styles.metricCard} ${styles.metricCardPendente}`}>
          <span className={`${styles.metricDot} ${styles.dotPendente}`}></span>
          <h3 className={styles.metricValue}>{pedidosPendentes.length}</h3>
          <p className={`${styles.metricLabel} ${styles.labelPendente}`}>Pendente</p>
        </div>

        <div className={styles.metricCard}>
          <span className={`${styles.metricDot} ${styles.dotAndamento}`}></span>
          <h3 className={styles.metricValue}>{pedidosEmAndamento}</h3>
          <p className={styles.metricLabel}>Andamento</p>
        </div>

        <div className={styles.metricCard}>
          <span className={`${styles.metricDot} ${styles.dotPronto}`}></span>
          <h3 className={styles.metricValue}>{pedidosProntos}</h3>
          <p className={styles.metricLabel}>Pronto</p>
        </div>
      </section>

      {/* Atalhos rápidos */}
      <section className={styles.atalhosGrid}>
        {ATALHOS.map(({ to, label, icon: Icon }) => (
          <Link key={to} to={to} className={styles.atalhoChip}>
            <span className={styles.atalhoIcon}><Icon size={17} /></span>
            {label}
          </Link>
        ))}
      </section>

      {/* Seção de Pedidos Pendentes */}
      <section className={styles.pedidosSection}>
        <div className={styles.pedidosSectionHeader}>
          <h2 className={styles.pedidosSectionTitle}>Pedidos</h2>
          <span className={styles.pedidosSectionBadge}>
            <span className={styles.pedidosSectionBadgeDot}></span>
            Pendente
          </span>
        </div>
        <p className={styles.pedidosSectionDesc}>Aceite os pedidos abaixo para iniciar o preparo</p>

        <div className={styles.pedidosContainer}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p className={styles.loading}>Carregando pedidos...</p>
            </div>
          ) : pedidosPendentesOrdenados.length > 0 ? (
            pedidosPendentesOrdenados.map(pedido => (
              <div key={pedido.id_pedido} className={styles.pedidoPendenteWrapper}>
                <CardUltimosPedidos
                  pedido={pedido}
                  exibirNomes={true}
                  mostrarBotoesStatus={false}
                  mostrarClienteEStatus={true}
                  mudarStatusPedido={mudarStatusPedido}
                />
                <button
                  className={styles.btnAceitarPedido}
                  onClick={() => aceitarPedido(pedido)}
                >
                  Aceitar Pedido
                </button>
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>
              <Receipt size={40} className={styles.emptyIcon} />
              <p className={styles.emptySubtitle}>nenhum pedido pendente no momento</p>
            </div>
          )}
        </div>
      </section>

      <FooterOpcoes typeView="equipe"/>
    </div>
  );
};

export default HomeEquipe;
